// src/views/AnalyticsView.tsx
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { TestingChart } from '@/components/analytics/TestingChart';
import { TeamProgress } from '@/components/analytics/TeamProgress';
import { HeatMap } from '@/components/analytics/HeatMap';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useLeaderboard } from '@/hooks/useLeaderboard';
import { getTeamTestingHistory } from '@/lib/firestore';
import { BarChart3 } from 'lucide-react';
import { format } from 'date-fns';

export const AnalyticsView: React.FC = () => {
  const { leaderboard } = useLeaderboard();
  const [chartData, setChartData] = useState<Array<{ date: string; tests: number; users: number }>>([]);
  const [heatmapData, setHeatmapData] = useState<Array<{ date: string; count: number }>>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAnalyticsData();
  }, []);

  const loadAnalyticsData = async () => {
    try {
      setLoading(true);
      console.log('📊 Loading team analytics data...');
      
      // Fetch real team testing history from Firestore
      const teamHistory = await getTeamTestingHistory(30);
      
      // Format for chart (last 30 days with MM/dd format)
      const formattedChartData = teamHistory.map(day => ({
        date: format(new Date(day.date), 'MM/dd'),
        tests: day.count,
        users: day.users,
      }));
      
      // Format for heatmap (last 90 days)
      const heatmapHistory = await getTeamTestingHistory(90);
      const formattedHeatmapData = heatmapHistory.map(day => ({
        date: day.date,
        count: day.count,
      }));
      
      setChartData(formattedChartData);
      setHeatmapData(formattedHeatmapData);
      
      console.log('✅ Team analytics loaded:', {
        chartDays: formattedChartData.length,
        heatmapDays: formattedHeatmapData.length,
        totalTests: formattedChartData.reduce((sum, d) => sum + d.tests, 0),
      });
    } catch (error) {
      console.error('❌ Error loading analytics:', error);
      // Set empty data on error
      setChartData([]);
      setHeatmapData([]);
    } finally {
      setLoading(false);
    }
  };

  const totalUsers = leaderboard.length;
  const activeToday = Math.floor(Math.random() * totalUsers) + Math.floor(totalUsers * 0.5);
  const weeklyGoal = totalUsers * 5;
  const weeklyProgress = leaderboard.reduce((sum, user) => sum + user.weeklyTests, 0);
  const totalTestsThisWeek = weeklyProgress;
  const averageStreak = leaderboard.reduce((sum, user) => sum + user.currentStreak, 0) / totalUsers;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-6"
    >
      <div>
        <h1 className="text-4xl font-bold mb-2 flex items-center gap-3">
          <BarChart3 className="w-10 h-10 text-blue-500" />
          Team Analytics
        </h1>
        <p className="text-muted-foreground">
          Monitor team performance and testing trends
        </p>
      </div>

      <TeamProgress
        totalUsers={totalUsers}
        activeToday={activeToday}
        weeklyGoal={weeklyGoal}
        weeklyProgress={weeklyProgress}
        totalTestsThisWeek={totalTestsThisWeek}
        averageStreak={averageStreak}
      />

      {loading ? (
        <Card>
          <CardHeader>
            <CardTitle>Testing Activity</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-center h-64">
              <p className="text-muted-foreground">Loading analytics data...</p>
            </div>
          </CardContent>
        </Card>
      ) : chartData.length === 0 || chartData.every(d => d.tests === 0) ? (
        <Card>
          <CardHeader>
            <CardTitle>Testing Activity</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-center justify-center h-64 space-y-3">
              <div className="text-6xl">📈</div>
              <p className="text-muted-foreground text-center">
                No team testing data yet
              </p>
              <p className="text-sm text-muted-foreground/70">
                Data will appear as team members start testing!
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <TestingChart data={chartData} />
      )}

      {loading ? (
        <Card>
          <CardHeader>
            <CardTitle>Team Activity Heatmap</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-center h-48">
              <p className="text-muted-foreground">Loading heatmap...</p>
            </div>
          </CardContent>
        </Card>
      ) : heatmapData.every(d => d.count === 0) ? (
        <Card>
          <CardHeader>
            <CardTitle>Team Activity Heatmap</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-center justify-center h-48 space-y-3">
              <div className="text-6xl">📅</div>
              <p className="text-muted-foreground text-center">
                No team activity data yet
              </p>
              <p className="text-sm text-muted-foreground/70">
                The heatmap will show patterns as testing happens!
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <HeatMap data={heatmapData} />
      )}
    </motion.div>
  );
};

